import { DateInput, TextInput } from '@core/components/Inputs';
import { SelectSimple } from '@core/components/Select';
import { useApiUtils } from '@core/util/apiUtils';
import { formatCNPJ, formatCPF } from '@format';
import { FormControl, FormHelperText, Grid, MenuItem, Select, Tooltip } from '@material-ui/core';
import { Help } from '@material-ui/icons';
import SelectCnae from 'app/components/Select/SelectCnae';
import { getValue } from 'app/main/empresa/modal/enums/Comercializacao';
import empresaOnboardingApi from 'app/services/api/empresaOnboardngApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { encrypt } from 'app/utils/geralUtil';
import { useFormik } from 'formik';
import React, { useContext, useEffect } from 'react';
import 'react-image-crop/dist/ReactCrop.css';
import { useDispatch } from 'react-redux';
import { Comercializacao } from '../../empresa/modal/enums/';
import { OnboardingContext } from '../OnboardingContext';
import OnboardingLayout from '../OnboardingLayout';
import { useOnboardingSchemaDadosCadastrais } from '../schemas/OnboardingSchemas';
import InformativoLateral from './InformativoLateral';

const DadosCadastraisOnboarding = () => {
  const [, setAtualizarEtapa] = useContext(OnboardingContext).atualizarEtapa;
  const [empresaAtual] = useContext(GlobalContext).empresa;
  const [, setLoading] = useContext(OnboardingContext).loading;
  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const initialValues = {
    id: empresaAtual?.empresaId,
    comercializacao: empresaAtual?.comercializacao || null,
    regimeTributario: 'NENHUM',
    regimeTributarioEspecial: 'SEM_REGIME_TRIBUTARIO',
    cnae: empresaAtual?.cnae || {},
    pessoa: {
      id: empresaAtual?.pessoa?.id,
      nome: empresaAtual?.pessoa?.nome,
      tipoPessoa: empresaAtual?.pessoa?.tipoPessoa || 'JURIDICA',
      inscricaoMunicipal: empresaAtual?.pessoa?.inscricaoMunicipal,
      pessoaJuridica: {
        id: empresaAtual?.pessoa?.pessoaJuridica?.id,
        razaoSocial: empresaAtual?.pessoa?.pessoaJuridica?.razaoSocial,
        cnpj: empresaAtual?.pessoa?.pessoaJuridica?.cnpj,
        inscricaoEstadual: empresaAtual?.pessoa?.pessoaJuridica?.inscricaoEstadual,
      },
      pessoaFisica: {
        id: empresaAtual?.pessoa?.pessoaJuridica?.id,
        cpf: empresaAtual?.pessoa?.pessoaFisica?.cpf,
        identidade: empresaAtual?.pessoa?.pessoaFisica?.identidade,
        dataNascimento: empresaAtual?.pessoa?.pessoaFisica?.dataNascimento,
        identificadorEstrangeiro: empresaAtual?.pessoa?.pessoaFisica?.identificadorEstrangeiro,
      },
    },
  };

  const formik = useFormik({
    validationSchema: useOnboardingSchemaDadosCadastrais(),
    initialValues,
    onSubmit: () => null,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
  });

  useEffect(() => {
    formik.resetForm(initialValues);
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaAtual]);

  const handleSubmit = async () => {
    formik.submitForm();

    if (!formik.isValid) {
      return;
    }

    setLoading(true);

    const empresaId = empresaAtual?.empresaId || '';
    const cnaeId = formik.values.cnae?.id;
    const requestObject = { empresaRequest: formik.values };

    if (formik.values?.pessoa?.tipoPessoa === 'JURIDICA') {
      if (formik.values?.pessoa?.pessoaJuridica?.cnpj) {
        requestObject.empresaRequest.pessoa.pessoaJuridica.cnpj = formik.values?.pessoa?.pessoaJuridica?.cnpj
          .replaceAll('.', '')
          .replaceAll('/', '')
          .replaceAll('-', '');
      }

      delete requestObject.empresaRequest?.pessoa?.pessoaFisica;
    } else if (formik.values?.pessoa?.tipoPessoa === 'FISICA') {
      if (formik.values?.pessoa?.pessoaFisica?.cpf) {
        requestObject.empresaRequest.pessoa.pessoaFisica.cpf = formik.values?.pessoa?.pessoaFisica?.cpf
          .replaceAll('.', '')
          .replaceAll('-', '');
      }

      delete requestObject.empresaRequest?.pessoa?.pessoaJuridica;
    }

    delete requestObject.empresaRequest.cnae;
    requestObject.empresaRequest.cnaeId = cnaeId || null;

    try {
      let resp = await empresaOnboardingApi.atualizar(empresaId, 'DADOS_CADASTRAIS', requestObject);
      if (resp.data) {
        localStorage.setItem('EC', encrypt(resp.data));
        setAtualizarEtapa(true);
        setLoading(false);
      }
    } catch (err) {
      apiUtils.handleApiError(err);
      setLoading(false);
    }
  };

  const _errors = {
    comercializacao: (formik.submitCount > 0 || formik.touched?.comercializacao) && formik.errors?.comercializacao,
    pessoa: {
      nome: (formik.submitCount > 0 || formik.touched?.pessoa?.nome) && formik.errors?.pessoa?.nome,
      tipoPessoa: (formik.submitCount > 0 || formik.touched?.pessoa?.tipoPessoa) && formik.errors?.pessoa?.tipoPessoa,
      inscricaoMunicipal:
        (formik.submitCount > 0 || formik.touched?.pessoa?.inscricaoMunicipal) &&
        formik.errors?.pessoa?.inscricaoMunicipal,
      pessoaJuridica: {
        cnpj:
          (formik.submitCount > 0 || formik.touched?.pessoa?.pessoaJuridica?.cnpj) &&
          formik.errors?.pessoa?.pessoaJuridica?.cnpj,
        razaoSocial:
          (formik.submitCount > 0 || formik.touched?.pessoa?.pessoaJuridica?.razaoSocial) &&
          formik.errors?.pessoa?.pessoaJuridica?.razaoSocial,
        inscricaoEstadual:
          (formik.submitCount > 0 || formik.touched?.pessoa?.pessoaJuridica?.inscricaoEstadual) &&
          formik.errors?.pessoa?.pessoaJuridica?.inscricaoEstadual,
      },
      pessoaFisica: {
        cpf:
          (formik.submitCount > 0 || formik.touched?.pessoa?.pessoaFisica?.cpf) &&
          formik.errors?.pessoa?.pessoaFisica?.cpf,
        identidade:
          (formik.submitCount > 0 || formik.touched?.pessoa?.pessoaFisica?.identidade) &&
          formik.errors?.pessoa?.pessoaFisica?.identidade,
        dataNascimento:
          (formik.submitCount > 0 || formik.touched?.pessoa?.pessoaFisica?.dataNascimento) &&
          formik.errors?.pessoa?.pessoaFisica?.dataNascimento,
        identificadorEstrangeiro:
          (formik.submitCount > 0 || formik.touched?.pessoa?.pessoaFisica?.identificadorEstrangeiro) &&
          formik.errors?.pessoa?.pessoaFisica?.identificadorEstrangeiro,
      },
    },
  };

  const renderCampoInscricaoMunicipal = () => {
    return (
      <>
        <div style={{ marginBottom: '7px' }}>Inscrição municipal</div>
        <TextInput
          id="pessoa.inscricaoMunicipal"
          placeholder={'Número'}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={_errors.pessoa?.inscricaoMunicipal}
          touched={!!_errors.pessoa.inscricaoMunicipal}
          value={formik.values?.pessoa?.inscricaoMunicipal}
          maxLength={20}
        />
      </>
    );
  };

  const renderCampoComercializacao = () => {
    return (
      <>
        <div style={{ marginBottom: '7px' }}>Comercialização *</div>
        <FormControl variant="outlined" fullWidth>
          <Select
            id="comercializacao"
            name="comercializacao"
            value={formik.values?.comercializacao}
            error={_errors.comercializacao}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            displayEmpty
            renderValue={(selected) => {
              if (selected && selected.length > 0) {
                return getValue(selected);
              }
              return <span style={{ color: '#abaaaa' }}>Selecione</span>;
            }}
            required
          >
            {comercializacaoValues.map((option) => {
              return (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              );
            })}
          </Select>
          <FormHelperText style={{ color: '#f44336' }}>{_errors.comercializacao}</FormHelperText>
        </FormControl>
      </>
    );
  };

  return (
    <OnboardingLayout
      onSubmit={handleSubmit}
      wizardStep={0}
      leftSidebarContent={
        <InformativoLateral titulo="Sua empresa" subTitulo="Dados cadastrais" imagem="assets/images/onboarding/1.svg">
          Informe os dados da sua empresa para começar a utilizar o sistema financeiro e aproveitar as diversas
          facilidades que vamos oferecer para a gestão da sua empresa.
        </InformativoLateral>
      }
      content={
        <>
          <Grid container>
            <Grid item lg={2} md={2} sm={2} xs={2} />
            <Grid item lg={8} md={8} sm={8} xs={8}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={8} lg={8}>
                  <div style={{ marginBottom: '7px' }}>Nome da sua empresa *</div>
                  <TextInput
                    autoFocus
                    id="pessoa.nome"
                    placeholder={'Nome'}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values?.pessoa?.nome}
                    touched={!!_errors.pessoa?.nome}
                    error={_errors.pessoa?.nome}
                    required
                    maxLength={100}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={4}>
                  <div style={{ marginBottom: '7px' }}>Tipo da pessoa *</div>
                  <SelectSimple
                    defaultValue="JURIDICA"
                    id="pessoa.tipoPessoa"
                    onChange={formik.handleChange}
                    value={formik.values?.pessoa?.tipoPessoa}
                    touched={!!_errors.pessoa?.tipoPessoa}
                    error={_errors.pessoa?.tipoPessoa}
                    options={[
                      { title: 'Pessoa Jurídica', id: 'JURIDICA' },
                      { title: 'Pessoa Física', id: 'FISICA' },
                    ]}
                  />
                </Grid>
                {formik?.values?.pessoa?.tipoPessoa === 'JURIDICA' && (
                  <>
                    <Grid item xs={12} sm={12} md={8} lg={8}>
                      <div style={{ marginBottom: '7px' }}>Razão social</div>
                      <TextInput
                        id="pessoa.pessoaJuridica.razaoSocial"
                        placeholder={'Razão social da sua empresa'}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={_errors.pessoa.pessoaJuridica.razaoSocial}
                        touched={!!_errors.pessoa.pessoaJuridica.razaoSocial}
                        value={formik.values?.pessoa?.pessoaJuridica?.razaoSocial}
                        maxLength={150}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      <div style={{ marginBottom: '7px' }}>CNPJ</div>
                      <TextInput
                        id="pessoa.pessoaJuridica.cnpj"
                        placeholder={'Digite o número do seu CNPJ'}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        touched={!!_errors.pessoa?.pessoaJuridica?.cnpj}
                        error={_errors.pessoa?.pessoaJuridica?.cnpj}
                        value={formatCNPJ(formik.values?.pessoa?.pessoaJuridica?.cnpj)}
                        maxLength={18}
                      />
                    </Grid>
                  </>
                )}

                {formik?.values?.pessoa?.tipoPessoa === 'FISICA' && (
                  <>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      <div style={{ marginBottom: '7px' }}>CPF</div>
                      <TextInput
                        id="pessoa.pessoaFisica.cpf"
                        placeholder={'Digite o número do seu CPF'}
                        value={formatCPF(formik?.values?.pessoa?.pessoaFisica?.cpf)}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        maxLength={14}
                        touched={!!_errors.pessoa?.pessoaFisica?.cpf}
                        error={_errors.pessoa?.pessoaFisica?.cpf}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      <div style={{ marginBottom: '7px' }}>Identidade</div>
                      <TextInput
                        id="pessoa.pessoaFisica.identidade"
                        placeholder={'Número da identidade'}
                        value={formik?.values?.pessoa?.pessoaFisica?.identidade}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        maxLength={14}
                        touched={!!_errors.pessoa?.pessoaFisica?.identidade}
                        error={_errors.pessoa?.pessoaFisica?.identidade}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      <div style={{ marginBottom: '7px' }}>Data de nascimento</div>
                      <DateInput
                        fullWidth
                        disableFuture
                        id="pessoa.pessoaFisica.dataNascimento"
                        name="pessoa.pessoaFisica.dataNascimento"
                        value={formik?.values?.pessoa?.pessoaFisica?.dataNascimento}
                        onChange={formik.handleChange}
                        touched={!!_errors.dataNascimento}
                        error={_errors.dataNascimento}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      <div style={{ marginBottom: '7px' }}>Identificador estrangeiro</div>
                      <TextInput
                        id="pessoa.pessoaFisica.identificadorEstrangeiro"
                        placeholder={'Identificador estrangeiro'}
                        value={formik?.values?.pessoa?.pessoaFisica?.identificadorEstrangeiro}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        touched={!!_errors.pessoa?.pessoaFisica?.identificadorEstrangeiro}
                        error={_errors.pessoa?.pessoaFisica?.identificadorEstrangeiro}
                        maxLength={15}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      {renderCampoInscricaoMunicipal()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      {renderCampoComercializacao()}
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <div style={{ marginBottom: '7px' }}>
                    <span>Principal atividade econômica</span>
                    <span style={{ float: 'right' }}>
                      <Tooltip placement="top-end" title="Informe o CNAE principal da empresa.">
                        <Help className="icone-informativo size-18" />
                      </Tooltip>
                    </span>
                  </div>
                  <SelectCnae
                    id="cnae"
                    label=""
                    value={formik.values?.cnae}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                {formik?.values?.pessoa?.tipoPessoa === 'JURIDICA' && (
                  <>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      <div style={{ marginBottom: '7px' }}>Inscrição estadual</div>
                      <TextInput
                        id="pessoa.pessoaJuridica.inscricaoEstadual"
                        placeholder={'Número'}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        replace={{ searchValue: /[^0-9]/gm, newValue: '' }}
                        pattern={/^[0-9]*$/gm}
                        error={_errors.pessoa?.pessoaJuridica?.inscricaoEstadual}
                        touched={!!_errors.pessoa?.pessoaJuridica?.inscricaoEstadual}
                        value={formik.values?.pessoa?.pessoaJuridica?.inscricaoEstadual}
                        maxLength={20}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      {renderCampoInscricaoMunicipal()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                      {renderCampoComercializacao()}
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            <Grid item lg={2} md={2} sm={2} xs={2} />
          </Grid>
        </>
      }
    />
  );
};

const comercializacaoValues = [
  {
    value: 'SERVICO',
    label: Comercializacao.SERVICO,
  },
  {
    value: 'PRODUTO',
    label: Comercializacao.PRODUTO,
  },
  {
    value: 'AMBOS',
    label: Comercializacao.AMBOS,
  },
];

export default DadosCadastraisOnboarding;
